/**
 * NativeLeaguesScreen — full-screen "Your Leagues" list for the native tab bar.
 *
 * Mirrors what Sidebar.jsx shows on web (My Leagues list + Add League), as a
 * dedicated screen rather than a shortcut that jumps straight to a league —
 * requested directly, so the tab always shows the full picture instead of
 * behaving differently depending on how many leagues are connected.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLeagues } from '../context/LeagueContext'
import { useAuth } from '../context/AuthContext'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'
import AddLeagueModal from './AddLeagueModal'
import RemoveLeagueModal from './RemoveLeagueModal'

const ICON = 20

// Same two ball glyphs Sidebar.jsx uses, so a league reads the same way here
// as it does on the web sidebar.
const sportIcons = {
  nba: (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6.3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 1.7v12.6M1.7 8h12.6" stroke="currentColor" strokeWidth="1.1" opacity="0.75" />
      <path d="M3.55 3.55a8.5 8.5 0 0 0 8.9 8.9M12.45 3.55a8.5 8.5 0 0 0-8.9 8.9"
        stroke="currentColor" strokeWidth="1.1" opacity="0.75" fill="none" />
    </svg>
  ),
  nfl: (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M1.8 8C1.8 8 4.6 2.5 8 2.5s6.2 5.5 6.2 5.5-2.8 5.5-6.2 5.5S1.8 8 1.8 8Z"
        stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M6.6 6.9v2.2M8 6.7v2.6M9.4 6.9v2.2"
        stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.8" />
    </svg>
  ),
}

const sportIcon = (sport) =>
  String(sport || '').toLowerCase() === 'nfl' ? sportIcons.nfl : sportIcons.nba

const plusIcon = (
  <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none">
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

export default function NativeLeaguesScreen({ onClose }) {
  const { leagues, removeLeague } = useLeagues()
  const { refreshPlan } = useAuth()
  const navigate = useNavigate()
  const [showAddModal, setShowAddModal] = useState(false)
  const [leagueToDelete, setLeagueToDelete] = useState(null)

  const openLeague = (id) => {
    onClose()
    navigate(`/league/${id}`)
  }

  return (
    <div
      style={{
        // Sits below the tab bar's z-index (50) on purpose — the tab bar is
        // opaque, so it paints over this screen's bottom edge instead of the
        // screen needing to know the bar's exact height. Requested directly:
        // the three tabs stay visible and tappable while this is open, so
        // there's no separate close button here any more — tapping another
        // tab is how you leave.
        position: 'fixed', inset: 0, zIndex: 45,
        background: JAI_COLORS.navyDeep,
        display: 'flex', flexDirection: 'column',
        // Extra breathing room below the status bar — sitting right at the
        // safe-area edge read as too cramped/unbalanced.
        paddingTop: 'calc(var(--safe-top) + 28px)',
      }}
    >
      {/* Header */}
      <div
        className="flex-shrink-0"
        style={{
          padding: '0 20px 16px',
        }}
      >
        <h1 style={{ fontFamily: JAI_FONTS.tight, fontSize: 22, fontWeight: 800, color: 'white', margin: 0 }}>
          Your Leagues
        </h1>
      </div>

      {/* League list */}
      <div className="flex-1 overflow-y-auto" style={{ padding: '0 16px', paddingBottom: 'calc(var(--safe-bottom) + 88px)', WebkitOverflowScrolling: 'touch' }}>
        {leagues.length === 0 ? (
          <p style={{ fontFamily: JAI_FONTS.tight, fontSize: 14, color: 'rgba(246,248,251,0.65)', padding: '16px 4px' }}>
            No leagues yet — add one below to get personalised advice.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {leagues.map((league) => (
              <div key={league.id} style={{ position: 'relative' }}>
                <button
                  onClick={() => openLeague(league.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 44px 14px 14px', borderRadius: 14, textAlign: 'left',
                    background: JAI_COLORS.white, border: `1px solid ${JAI_COLORS.hair}`,
                    cursor: 'pointer', fontFamily: JAI_FONTS.tight,
                  }}
                >
                  <span style={{ color: JAI_COLORS.blue, display: 'flex', flexShrink: 0 }}>
                    {sportIcon(league.sport)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: JAI_COLORS.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {league.name}
                    </span>
                    <span style={{ display: 'block', fontSize: 13, color: JAI_COLORS.mute, marginTop: 1 }}>
                      {`${league.teamCount || '?'} teams · ${league.season || ''}`.replace(/ · $/, '')}
                    </span>
                  </span>
                </button>
                <button
                  onClick={() => setLeagueToDelete(league)}
                  aria-label={`Remove ${league.name}`}
                  style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', color: 'rgba(10,31,68,0.35)',
                    cursor: 'pointer', padding: 8, fontSize: 20, lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={() => setShowAddModal(true)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            marginTop: 12, padding: '14px', borderRadius: 14,
            background: JAI_COLORS.white, border: 'none',
            color: JAI_COLORS.navy, fontFamily: JAI_FONTS.tight, fontSize: 15, fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {plusIcon}
          Add League
        </button>
      </div>

      {showAddModal && (
        <AddLeagueModal
          onClose={() => setShowAddModal(false)}
          onAdded={(league) => { setShowAddModal(false); openLeague(league.id) }}
        />
      )}

      {leagueToDelete && (
        <RemoveLeagueModal
          league={leagueToDelete}
          onClose={() => setLeagueToDelete(null)}
          onRemoved={() => { removeLeague(leagueToDelete.id); setLeagueToDelete(null); refreshPlan?.() }}
        />
      )}
    </div>
  )
}
